import React, { useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  ExternalLink,
  Handshake,
  ImageOff,
  Loader2,
  RefreshCw,
  X,
} from "lucide-react";
import MarkThumbnail from "./MarkThumbnail";
import { StatusBadge } from "../ui/Badge";
import {
  EM_DASH,
  SIMILARITY_TEXT_CLASS,
  formatDate,
  humanize,
  officeLabel,
  registrationNumberOf,
  similarityTone,
} from "../../lib/format";

/* ---------- small layout pieces ---------- */

function Section({ title, children }) {
  return (
    <section className="rounded border border-ink-200 bg-white p-5">
      <h3 className="font-display text-lg font-semibold text-ink-900">
        {title}
      </h3>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Grid({ children }) {
  return (
    <dl className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
      {children}
    </dl>
  );
}

/** Label above value; a missing value reads "N/A" rather than leaving a hole. */
function Field({ label, value, children, mono = false, span = false }) {
  const content = children ?? value;
  const empty = content === undefined || content === null || content === "";
  return (
    <div className={`min-w-0 ${span ? "sm:col-span-2" : ""}`}>
      <dt className="text-sm font-semibold text-ink-900">{label}</dt>
      <dd
        className={`mt-0.5 break-words text-sm text-ink-700 ${mono ? "font-mono text-xs" : ""}`}
      >
        {empty ? <span className="text-ink-300">N/A</span> : content}
      </dd>
    </div>
  );
}

function Empty({ children }) {
  return <p className="text-sm text-ink-400">{children}</p>;
}

function SimpleTable({ columns, rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-ink-200 text-left">
            {columns.map((c) => (
              <th
                key={c.key}
                className="py-2 pr-4 text-sm font-semibold text-ink-900"
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-ink-100 last:border-b-0">
              {columns.map((c) => (
                <td key={c.key} className="py-2 pr-4 align-top text-ink-700">
                  {row[c.key] || EM_DASH}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const CONFLICT_TONE_CLASS = {
  opposed: "border-opposed/25 bg-opposed-soft",
  pending: "border-pending/25 bg-pending-soft",
  live: "border-live/25 bg-live-soft",
  none: "border-ink-200 bg-paper",
};

/**
 * Everything the register holds on one mark, laid out the way the USPTO
 * detail page lays out a case: what the mark is, what it covers, who owns
 * it, how it is described, who represents it, and what has happened to it.
 *
 * It opens instantly with the fields the search row already carried, then
 * fills in as the full record arrives (`detailState`). Every value that
 * isn't published says so, instead of leaving a blank you can't interpret.
 */
export default function TrademarkDetailModal({
  row,
  detailState = "none", // "loading" | "loaded" | "failed" | "unsupported" | "none"
  detailError,
  onRetry,
  similarityScore,
  reasoning,
  position, // { index, total } — 1-based
  onPrev,
  onNext,
  onClose,
  onOpenImage,
  onFindLicensing,
}) {
  const [copied, setCopied] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    function onKeyDown(event) {
      if (event.key === "Escape") onClose();
      else if (event.key === "ArrowLeft" && onPrev) onPrev();
      else if (event.key === "ArrowRight" && onNext) onNext();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose, onPrev, onNext]);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  // Moving to the next mark should start at the top, not mid-scroll.
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [row?.trademarkId]);

  if (!row) return null;

  const detail = row.detail;
  const info = detail?.markInfo || {};
  const fallback = row.details || {};

  const owners = detail?.owners?.length
    ? detail.owners
    : row.applicantName
      ? [
          {
            name: row.applicantName,
            address: fallback.applicantAddress || "",
            country: "",
            entityType: "",
            identifier: "",
          },
        ]
      : [];
  const representatives = detail?.representatives?.length
    ? detail.representatives
    : fallback.representative
      ? [{ name: fallback.representative, address: "", country: "" }]
      : [];
  const goods = (row.goodsAndServices || []).filter(
    (g) => g.classNumber || g.description,
  );
  const vienna = info.viennaClasses?.length
    ? info.viennaClasses
    : fallback.viennaClasses || [];
  const claims = [
    ...(detail?.priorities?.length
      ? detail.priorities.map((c) => ({ ...c, type: "Priority" }))
      : fallback.priorityDate || fallback.priorityCountry
        ? [
            {
              type: "Priority",
              date: fallback.priorityDate,
              country: fallback.priorityCountry,
              number: "",
            },
          ]
        : []),
    ...(detail?.seniorities || []).map((c) => ({ ...c, type: "Seniority" })),
  ].map((c) => ({ ...c, date: c.date ? formatDate(c.date) : "" }));
  const events = (detail?.events || []).map((e) => ({
    ...e,
    date: e.date ? formatDate(e.date) : "",
  }));

  const registrationNumber = registrationNumberOf(row);
  const publicationDate = detail?.publicationDate || fallback.publicationDate;
  const tone = similarityTone(similarityScore);
  const hasSimilarity =
    similarityScore !== undefined && similarityScore !== null;
  const loading = detailState === "loading";

  function handleCopy() {
    if (!row.applicationNumber) return;
    navigator.clipboard
      ?.writeText(row.applicationNumber)
      .then(() => setCopied(true));
  }

  const openImage = row.imageUrl
    ? () => onOpenImage?.({ url: row.imageUrl, wordMark: row.wordMark })
    : undefined;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={
        row.wordMark ? `Details for ${row.wordMark}` : "Trade mark details"
      }
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/75 p-3 backdrop-blur-sm sm:p-6"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[94vh] w-full max-w-5xl flex-col overflow-hidden rounded-soft bg-paper shadow-float"
      >
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-ink-200 bg-white px-5 py-3.5">
          <div className="min-w-0 flex-1">
            {position && (
              <p className="text-xs text-ink-500">
                Result{" "}
                <span className="font-semibold text-ink-800">
                  {position.index}
                </span>{" "}
                of {position.total}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate font-display text-xl font-semibold text-ink-900">
                {row.wordMark || EM_DASH}
              </h2>
              <StatusBadge status={row.status} />
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={onPrev}
              disabled={!onPrev}
              aria-label="Previous result"
              title="Previous result (←)"
              className="rounded p-1.5 text-ink-600 hover:bg-ink-100 disabled:text-ink-200 disabled:hover:bg-transparent"
            >
              <ChevronLeft className="h-5 w-5" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={onNext}
              disabled={!onNext}
              aria-label="Next result"
              title="Next result (→)"
              className="rounded p-1.5 text-ink-600 hover:bg-ink-100 disabled:text-ink-200 disabled:hover:bg-transparent"
            >
              <ChevronRight className="h-5 w-5" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="ml-1 rounded p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div ref={bodyRef} className="space-y-4 overflow-y-auto p-4 sm:p-5">
          {loading && (
            <div className="flex items-center gap-2 rounded border border-ink-200 bg-white px-4 py-2.5 text-sm text-ink-600">
              <Loader2
                className="h-4 w-4 animate-spin text-ink-400"
                aria-hidden="true"
              />
              Loading the full record from the register — goods &amp; services,
              owners and history fill in as it arrives.
            </div>
          )}

          {detailState === "failed" && (
            <div className="flex flex-wrap items-center gap-3 rounded border border-pending/25 bg-pending-soft px-4 py-2.5 text-sm text-ink-800">
              <AlertTriangle
                className="h-4 w-4 shrink-0 text-pending"
                aria-hidden="true"
              />
              <span className="min-w-0 flex-1">
                Couldn't load the full record, so this shows only what the
                search returned.
                {detailError ? (
                  <span className="text-ink-500"> ({detailError})</span>
                ) : null}
              </span>
              {onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  className="inline-flex items-center gap-1.5 rounded border border-ink-300 bg-white px-2.5 py-1 text-xs font-semibold text-ink-800 hover:bg-ink-100"
                >
                  <RefreshCw className="h-3 w-3" aria-hidden="true" />
                  Retry
                </button>
              )}
            </div>
          )}

          {detailState === "unsupported" && (
            <p className="rounded border border-ink-200 bg-white px-4 py-2.5 text-sm text-ink-600">
              This record came from Signa, whose search already returns the full
              record — you're seeing the fields the search returned.
            </p>
          )}

          {hasSimilarity && (
            <div
              className={`rounded border px-4 py-3 ${CONFLICT_TONE_CLASS[tone]}`}
            >
              <div className="flex items-center justify-between gap-3">
                <p className="text-micro font-semibold uppercase tracking-wide text-ink-500">
                  AI conflict read
                </p>
                <span
                  className={`text-lg font-bold ${SIMILARITY_TEXT_CLASS[tone]}`}
                >
                  {similarityScore}% similar
                </span>
              </div>
              {reasoning && (
                <p className="mt-1.5 text-sm text-ink-800">{reasoning}</p>
              )}
            </div>
          )}

          {/* Trademark */}
          <Section title="Trademark">
            <div className="flex flex-col gap-5 sm:flex-row">
              <div className="sm:w-52 sm:shrink-0">
                <div className="flex">
                  <MarkThumbnail
                    wordMark={row.wordMark}
                    imageUrl={row.imageUrl}
                    size="card"
                    onClick={openImage}
                  />
                </div>
                {!row.imageUrl && (
                  <p className="mt-2 flex items-center gap-1.5 text-xs text-ink-400">
                    <ImageOff className="h-3.5 w-3.5" aria-hidden="true" />
                    No figurative image published.
                  </p>
                )}
              </div>

              <div className="min-w-0 flex-1">
                <Grid>
                  <Field label="Wordmark" value={row.wordMark} />
                  <Field label="Application number" mono>
                    {row.applicationNumber ? (
                      <span className="inline-flex items-center gap-1.5">
                        {row.applicationNumber}
                        <button
                          type="button"
                          onClick={handleCopy}
                          aria-label="Copy application number"
                          className="rounded p-0.5 text-ink-400 hover:bg-ink-100 hover:text-ink-700"
                        >
                          {copied ? (
                            <Check
                              className="h-3.5 w-3.5 text-live"
                              aria-hidden="true"
                            />
                          ) : (
                            <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                          )}
                        </button>
                      </span>
                    ) : null}
                  </Field>
                  <Field
                    label="Filing date"
                    value={row.filingDate ? formatDate(row.filingDate) : ""}
                  />
                  <Field
                    label="Registration number"
                    value={registrationNumber}
                    mono
                  />
                  <Field
                    label="Registration date"
                    value={
                      row.registrationDate
                        ? formatDate(row.registrationDate)
                        : ""
                    }
                  />
                  <Field
                    label="Publication date"
                    value={publicationDate ? formatDate(publicationDate) : ""}
                  />
                  <Field label="Status">
                    <span className="inline-flex flex-wrap items-center gap-2">
                      <StatusBadge status={row.status} />
                      {detail?.statusDate && (
                        <span className="text-xs text-ink-500">
                          since {formatDate(detail.statusDate)}
                        </span>
                      )}
                    </span>
                  </Field>
                  <Field
                    label="Expiry date"
                    value={row.expiryDate ? formatDate(row.expiryDate) : ""}
                  />
                  <Field
                    label="Register"
                    value={officeLabel(row.jurisdiction)}
                  />
                  <Field
                    label="Source"
                    value={(row.source || "").toUpperCase()}
                  />
                  {detail?.statusDescription && (
                    <Field
                      label="Case status"
                      value={detail.statusDescription}
                      span
                    />
                  )}
                  <Field label="Class" span>
                    {row.niceClasses?.length ? (
                      <span className="flex flex-wrap gap-1.5">
                        {row.niceClasses.map((cls) => (
                          <span
                            key={cls}
                            className="rounded border border-ink-200 bg-paper px-1.5 py-0.5 font-mono text-xs text-ink-700"
                          >
                            {cls}
                          </span>
                        ))}
                      </span>
                    ) : null}
                  </Field>
                </Grid>
              </div>
            </div>
          </Section>

          {/* Goods and services */}
          <Section title="Goods and services">
            {goods.length ? (
              <ul className="space-y-3">
                {goods.map((g, i) => (
                  <li
                    key={`${g.classNumber}-${i}`}
                    className="flex gap-3 text-sm"
                  >
                    {g.classNumber ? (
                      <span className="mt-0.5 h-fit shrink-0 rounded border border-ink-200 bg-paper px-2 py-0.5 font-mono text-xs font-semibold text-ink-800">
                        IC {g.classNumber}
                      </span>
                    ) : null}
                    <p className="min-w-0 break-words text-ink-700">
                      {g.description || EM_DASH}
                    </p>
                  </li>
                ))}
              </ul>
            ) : loading ? (
              <div
                className="space-y-2"
                role="status"
                aria-label="Loading goods and services"
              >
                <div className="h-3.5 w-full animate-pulse rounded bg-ink-100" />
                <div className="h-3.5 w-3/4 animate-pulse rounded bg-ink-100" />
              </div>
            ) : (
              <Empty>
                The register didn't publish a goods and services description for
                this mark.
              </Empty>
            )}
          </Section>

          {/* Current owner */}
          <Section title="Current owner">
            {owners.length ? (
              <div className="divide-y divide-ink-100">
                {owners.map((owner, i) => (
                  <div
                    key={`${owner.name}-${i}`}
                    className={i > 0 ? "pt-4" : ""}
                  >
                    <Grid>
                      <Field label="Name" value={owner.name} />
                      <Field label="Address" value={owner.address} />
                      <Field
                        label="Entity type"
                        value={humanize(owner.entityType)}
                      />
                      <Field label="Country" value={owner.country} />
                      {owner.identifier ? (
                        <Field
                          label="Register ID"
                          value={owner.identifier}
                          mono
                        />
                      ) : null}
                    </Grid>
                  </div>
                ))}
              </div>
            ) : loading ? (
              <div
                className="h-3.5 w-1/2 animate-pulse rounded bg-ink-100"
                role="status"
                aria-label="Loading owner"
              />
            ) : (
              <Empty>No owner is published for this mark.</Empty>
            )}
          </Section>

          {/* Mark information */}
          <Section title="Mark information">
            <Grid>
              <Field label="Mark literal element" value={row.wordMark} />
              <Field label="Mark type" value={humanize(row.markFeature)} />
              <Field label="Mark kind" value={humanize(detail?.markKind)} />
              <Field label="Mark basis" value={humanize(detail?.markBasis)} />
              <Field
                label="Description"
                value={info.description || fallback.markDescription}
                span
              />
              <Field label="Translation" value={info.translation} />
              <Field label="Transliteration" value={info.transliteration} />
              <Field label="Disclaimer" value={info.disclaimer} span />
              <Field label="Colour claim" value={info.colourClaim} />
              <Field
                label="Filing language"
                value={detail?.applicationLanguage}
              />
              <Field label="Second language" value={detail?.secondLanguage} />
              <Field label="Design (Vienna) codes" span>
                {vienna.length ? (
                  <span className="flex flex-wrap gap-1.5">
                    {vienna.map((code) => (
                      <span
                        key={code}
                        className="rounded border border-ink-200 bg-paper px-1.5 py-0.5 font-mono text-xs text-ink-700"
                      >
                        {code}
                      </span>
                    ))}
                  </span>
                ) : null}
              </Field>
            </Grid>
          </Section>

          {/* Representative */}
          <Section title="Representative">
            {representatives.length ? (
              <div className="divide-y divide-ink-100">
                {representatives.map((rep, i) => (
                  <div key={`${rep.name}-${i}`} className={i > 0 ? "pt-4" : ""}>
                    <Grid>
                      <Field label="Name" value={rep.name} />
                      <Field label="Address" value={rep.address} />
                    </Grid>
                  </div>
                ))}
              </div>
            ) : (
              <Empty>No representative is published for this mark.</Empty>
            )}
          </Section>

          {/* Priority and seniority */}
          <Section title="Priority and seniority">
            {claims.length ? (
              <SimpleTable
                columns={[
                  { key: "type", label: "Claim" },
                  { key: "date", label: "Date" },
                  { key: "country", label: "Country / office" },
                  { key: "number", label: "Number" },
                ]}
                rows={claims}
              />
            ) : (
              <Empty>No priority or seniority is claimed.</Empty>
            )}
          </Section>

          {/* History */}
          <Section title="History">
            {events.length ? (
              <SimpleTable
                columns={[
                  { key: "date", label: "Date" },
                  { key: "description", label: "Description" },
                ]}
                rows={events}
              />
            ) : loading ? (
              <div
                className="h-3.5 w-2/3 animate-pulse rounded bg-ink-100"
                role="status"
                aria-label="Loading history"
              />
            ) : (
              <Empty>The register didn't return a history for this mark.</Empty>
            )}
          </Section>

          {/* Renewal */}
          <Section title="Renewal and maintenance">
            <Grid>
              <Field
                label="Expiry date"
                value={row.expiryDate ? formatDate(row.expiryDate) : ""}
              />
              <Field
                label="Last renewal"
                value={
                  detail?.renewalDate ? formatDate(detail.renewalDate) : ""
                }
              />
            </Grid>
          </Section>

          {/* Raw record — for checking what the register actually sent. */}
          <details className="rounded border border-ink-200 bg-white p-4">
            <summary className="cursor-pointer text-sm font-semibold text-ink-900">
              All fields from the register
              <span className="ml-2 text-xs font-normal text-ink-400">
                {detail ? "full record" : "search result only"}
              </span>
            </summary>
            <pre className="mt-3 max-h-96 overflow-auto rounded bg-ink-950 p-3 text-micro leading-relaxed text-ink-100">
              {JSON.stringify(detail?.raw ?? row.raw ?? {}, null, 2)}
            </pre>
          </details>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-ink-200 bg-white px-5 py-3">
          {onFindLicensing && (
            <button
              type="button"
              onClick={() => onFindLicensing(row)}
              className="inline-flex h-9 items-center gap-1.5 rounded border border-ink-200 bg-white px-3.5 text-sm font-semibold text-ink-800 hover:border-ink-300 hover:bg-ink-100/60"
            >
              <Handshake className="h-3.5 w-3.5" aria-hidden="true" />
              Find potential licensing
            </button>
          )}
          {row.imageUrl && (
            <button
              type="button"
              onClick={openImage}
              className="inline-flex h-9 items-center gap-1.5 rounded border border-ink-200 bg-white px-3.5 text-sm font-semibold text-ink-800 hover:border-ink-300 hover:bg-ink-100/60"
            >
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              View full logo
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 items-center rounded bg-ink-900 px-4 text-sm font-semibold text-white hover:bg-ink-800 active:bg-ink-950"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
